const FULLSCREEN_INTENT_KEY = 'mc-fullscreen-intent'
const FULLSCREEN_DISMISSED_KEY = 'mc-fullscreen-user-dismissed'
const FULLSCREEN_CHANGE_EVENT = 'mc-fullscreen-change'

function getFullscreenElement() {
  return (
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement ||
    null
  )
}

export function isTauriRuntime() {
  return typeof window !== 'undefined' && ('__TAURI_INTERNALS__' in window || '__TAURI__' in window)
}

async function getTauriWindow() {
  if (!isTauriRuntime()) return null
  try {
    const { getCurrentWindow } = await import('@tauri-apps/api/window')
    return getCurrentWindow()
  } catch {
    return null
  }
}

export function isDomFullscreenActive() {
  return Boolean(getFullscreenElement())
}

/** @deprecated Use isAppFullscreenActive for UI state. */
export function isFullscreenActive() {
  return isDomFullscreenActive()
}

async function isTauriFullscreenActive() {
  const win = await getTauriWindow()
  if (!win) return false
  try {
    return await win.isFullscreen()
  } catch {
    return false
  }
}

export async function isAppFullscreenActive() {
  if (isDomFullscreenActive()) return true
  return isTauriFullscreenActive()
}

export function getAppFullscreenTarget() {
  return document.getElementById('app') || document.documentElement
}

/** Teleport/dialog mount target — must stay inside the fullscreen subtree when active. */
export function getAppTeleportTarget() {
  return getFullscreenElement() || document.getElementById('app') || document.body
}

export function notifyAppFullscreenChange() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(FULLSCREEN_CHANGE_EVENT))
}

export function subscribeAppFullscreen(listener) {
  if (typeof window === 'undefined') return () => {}

  const onChange = () => listener()

  window.addEventListener(FULLSCREEN_CHANGE_EVENT, onChange)
  document.addEventListener('fullscreenchange', onChange)
  document.addEventListener('webkitfullscreenchange', onChange)

  let unlistenTauri = null
  getTauriWindow().then(async (win) => {
    if (!win) return
    try {
      unlistenTauri = await win.onResized(() => {
        notifyAppFullscreenChange()
      })
    } catch {
      // ignore listener errors
    }
  })

  return () => {
    window.removeEventListener(FULLSCREEN_CHANGE_EVENT, onChange)
    document.removeEventListener('fullscreenchange', onChange)
    document.removeEventListener('webkitfullscreenchange', onChange)
    if (typeof unlistenTauri === 'function') {
      unlistenTauri()
    }
  }
}

export function markFullscreenIntent(active) {
  try {
    if (active) {
      sessionStorage.setItem(FULLSCREEN_INTENT_KEY, '1')
    } else {
      sessionStorage.removeItem(FULLSCREEN_INTENT_KEY)
    }
  } catch {
    // ignore storage errors
  }
}

export function shouldRestoreFullscreen() {
  try {
    return sessionStorage.getItem(FULLSCREEN_INTENT_KEY) === '1'
  } catch {
    return false
  }
}

export function markUserDismissedFullscreen() {
  try {
    sessionStorage.setItem(FULLSCREEN_DISMISSED_KEY, '1')
  } catch {
    // ignore storage errors
  }
}

export function clearUserDismissedFullscreen() {
  try {
    sessionStorage.removeItem(FULLSCREEN_DISMISSED_KEY)
  } catch {
    // ignore storage errors
  }
}

export function hasUserDismissedFullscreen() {
  try {
    return sessionStorage.getItem(FULLSCREEN_DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

async function exitDomFullscreen() {
  if (!isDomFullscreenActive()) return

  try {
    if (document.exitFullscreen) {
      await document.exitFullscreen()
    } else if (document.webkitExitFullscreen) {
      await document.webkitExitFullscreen()
    } else if (document.msExitFullscreen) {
      await document.msExitFullscreen()
    }
  } catch (error) {
    console.warn('[Fullscreen] dom exit failed:', error)
  }
}

async function exitTauriFullscreen() {
  const win = await getTauriWindow()
  if (!win) return

  try {
    await win.setFullscreen(false)
  } catch (error) {
    console.warn('[Fullscreen] tauri exit failed:', error)
  }
}

async function enterTauriFullscreen() {
  const win = await getTauriWindow()
  if (!win) return false

  try {
    await win.setFullscreen(true)
    return await win.isFullscreen()
  } catch (error) {
    console.warn('[Fullscreen] tauri request failed:', error)
    return false
  }
}

async function enterDomFullscreen(target = getAppFullscreenTarget()) {
  if (!target) return false

  try {
    if (target.requestFullscreen) {
      await target.requestFullscreen()
    } else if (target.webkitRequestFullscreen) {
      await target.webkitRequestFullscreen()
    } else if (target.msRequestFullscreen) {
      await target.msRequestFullscreen()
    } else {
      return false
    }
    return isDomFullscreenActive()
  } catch (error) {
    console.warn('[Fullscreen] dom request failed:', error)
    return false
  }
}

export async function requestAppFullscreen(target = getAppFullscreenTarget()) {
  if (hasUserDismissedFullscreen()) return false

  let ok = false
  if (isTauriRuntime()) {
    ok = await enterTauriFullscreen()
  } else {
    ok = await enterDomFullscreen(target)
  }

  if (ok) notifyAppFullscreenChange()
  return ok
}

export async function exitAppFullscreen() {
  await exitDomFullscreen()
  await exitTauriFullscreen()
  notifyAppFullscreenChange()
}

export async function toggleAppFullscreen(target = getAppFullscreenTarget()) {
  if (await isAppFullscreenActive()) {
    markUserDismissedFullscreen()
    markFullscreenIntent(false)
    await exitAppFullscreen()
    return false
  }

  clearUserDismissedFullscreen()
  const ok = await requestAppFullscreen(target)
  markFullscreenIntent(ok)
  return ok
}

export async function restoreAppFullscreenIfNeeded(target = getAppFullscreenTarget()) {
  if (hasUserDismissedFullscreen()) return false
  if (!shouldRestoreFullscreen() || await isAppFullscreenActive()) return false

  const ok = await requestAppFullscreen(target)
  if (!ok) {
    markFullscreenIntent(false)
  }
  return ok
}

/** Request fullscreen on app launch; safe to call without user gesture in Tauri webview. */
export async function initDefaultAppFullscreen(target = getAppFullscreenTarget()) {
  if (hasUserDismissedFullscreen()) return false

  if (await isAppFullscreenActive()) {
    markFullscreenIntent(true)
    notifyAppFullscreenChange()
    return true
  }

  markFullscreenIntent(true)
  const ok = await requestAppFullscreen(target)

  if (hasUserDismissedFullscreen()) {
    await exitAppFullscreen()
    markFullscreenIntent(false)
    notifyAppFullscreenChange()
    return false
  }

  if (!ok) {
    markFullscreenIntent(false)
  }
  notifyAppFullscreenChange()
  return ok
}

export function bindFullscreenIntentSync() {
  return subscribeAppFullscreen(async () => {
    if (!(await isAppFullscreenActive())) {
      markFullscreenIntent(false)
    }
  })
}
